import type {
  CRMUser,
  ProductionCommentAccess,
  RfqProject,
  RfqSubmission,
  SupplierOrg,
  TimberProject,
} from '../types';
import {
  buildProductionCommentAccessDoc,
  supplierMatchesAwardedWork,
} from './productionCommentAccess';
import { isCrmTestUser, isCrmWorkstationUser } from './userRoles';

const HUB_INTERNAL_ROLES = new Set(['employee', 'admin', 'designer', 'engineer', 'staff']);

/**
 * Project Hub assignee / Action Required candidates: internal team plus
 * active supplier portal users awarded on the selected deal (same rule as
 * production @mentions).
 */
export function buildProjectHubTeamMembers(input: {
  crmUsers: CRMUser[];
  dealId?: string | null;
  productionCommentAccess?: ProductionCommentAccess[];
  rfqProjects?: RfqProject[];
  rfqSubmissions?: RfqSubmission[];
  timberProjects?: TimberProject[];
  supplierOrgs?: SupplierOrg[];
}): CRMUser[] {
  const internals = input.crmUsers.filter(
    (u) => HUB_INTERNAL_ROLES.has(u.role) && !isCrmTestUser(u) && !isCrmWorkstationUser(u),
  );
  const dealId = (input.dealId || '').trim();
  if (!dealId) return internals;

  const access =
    (input.productionCommentAccess || []).find((row) => row.dealId === dealId)
    || buildProductionCommentAccessDoc({
      dealId,
      projects: input.rfqProjects || [],
      submissions: input.rfqSubmissions || [],
      hubProjects: input.timberProjects || [],
    });

  const orgById = new Map<string, SupplierOrg>();
  const orgNameById = new Map<string, string>();
  for (const org of input.supplierOrgs || []) {
    if (!org.id) continue;
    orgById.set(org.id, org);
    if (org.name?.trim()) orgNameById.set(org.id, org.name.trim());
  }

  const awarded = input.crmUsers
    .filter((u) => {
      if (!u.isActive || u.role !== 'supplier' || !u.supplierOrgId || isCrmTestUser(u)) return false;
      const org = orgById.get(u.supplierOrgId);
      return supplierMatchesAwardedWork({
        orgId: u.supplierOrgId,
        flags: u.supplierDesignations,
        orgCapabilities: org?.capabilities,
        access,
      });
    })
    .map((u) => {
      const orgName = u.supplierOrgId ? orgNameById.get(u.supplierOrgId) : undefined;
      if (!orgName || (u.company || '').trim()) return u;
      return { ...u, company: orgName };
    });

  if (awarded.length === 0) return internals;

  const seen = new Set(internals.map((u) => u.uid));
  return [...internals, ...awarded.filter((u) => !seen.has(u.uid))];
}
